import { useCallback, useEffect, useMemo } from 'react'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { useQuery } from '@tanstack/react-query'
import { useTownsClient } from 'use-towns-client'

interface Review {
    id: string
    author: string
    rating: number
    text: string
    createdAt: number
}

export const useReviews = (spaceId: string | undefined) => {
    const { spaceDapp } = useTownsClient()

    const fetchReviews = useCallback(async (): Promise<Review[]> => {
        if (!spaceDapp) {
            throw new Error('Space not found')
        }

        const web3Space = spaceDapp.getSpace(spaceId!)
        if (!web3Space) {
            throw new Error('Space not found')
        }

        try {
            const [users, reviewsData] = await web3Space.Review.getAllReviews()

            return users.map((user, index) => {
                const review = reviewsData[index]
                return {
                    id: user,
                    author: user,
                    rating: review.rating,
                    text: review.comment,
                    createdAt: Number(review.createdAt),
                }
            })
        } catch (contractError) {
            // This is likely a contract not initialized error
            console.error('Contract error:', contractError)
            return []
        }
    }, [spaceId, spaceDapp])

    // Create error handler outside of query options
    const handleError = useCallback((error: Error) => {
        popupToast(({ toast }) => {
            return StandardToast({
                toast,
                message: 'Failed to load reviews',
                subMessage: error.message,
                icon: 'alert',
                iconProps: { color: 'error' },
                ctaColor: 'negative',
            })
        })
    }, [])

    const { data, error, ...queryRest } = useQuery<Review[]>({
        queryKey: ['reviews', spaceId],
        queryFn: fetchReviews,
        refetchOnWindowFocus: false,
        enabled: !!spaceId,
    })

    useEffect(() => {
        if (error) {
            handleError(error as Error)
        }
    }, [error, handleError])

    // Sort reviews by rating descending, then by createdAt descending
    const reviews = useMemo(
        () =>
            [...(data || [])].sort((a, b) => {
                if (b.rating !== a.rating) {
                    return b.rating - a.rating
                }
                return b.createdAt - a.createdAt
            }),
        [data],
    )

    const averageRating =
        reviews.length > 0
            ? reviews.reduce((acc: number, review: Review) => acc + review.rating, 0) /
              reviews.length
            : 0

    return {
        reviews,
        averageRating,
        totalReviews: reviews.length,
        error,
        ...queryRest,
    }
}
